import { useEffect, useMemo, useState } from 'react';
import { Bell, Check, ChevronDown, Clock, X } from 'lucide-react';
import api from '../../api/client';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Modal } from '../common/Modal';
import type { User } from '../../types';

interface ReminderModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  remindableType: 'task' | 'event';
  remindableId: number;
  remindableTitle: string;
  defaultRemindAt?: string;
  eligibleRecipients: User[];
}

const pad = (n: number) => n.toString().padStart(2, '0');

const toLocalInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function ReminderModal({
  open,
  onClose,
  onSaved,
  remindableType,
  remindableId,
  remindableTitle,
  defaultRemindAt,
  eligibleRecipients,
}: ReminderModalProps) {
  const [message, setMessage] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);
  const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMessage('');
      setRemindAt(defaultRemindAt || toLocalInput(new Date(Date.now() + 24 * 60 * 60 * 1000)));
      setSelectedRecipientIds([]);
      setError(null);
      setRecipientDropdownOpen(false);
    }
  }, [open, defaultRemindAt]);

  const backendType = useMemo(() => {
    return remindableType === 'task' ? 'App\\Models\\Task' : 'App\\Models\\TaskCalendarEvent';
  }, [remindableType]);

  const uniqueRecipients = useMemo(() => {
    const map = new Map<number, User>();
    eligibleRecipients.forEach((u) => {
      if (u && u.id != null) map.set(u.id, u);
    });
    return Array.from(map.values()).sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
  }, [eligibleRecipients]);

  const toggleRecipient = (id: number) => {
    setSelectedRecipientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!remindAt) return;
    setSaving(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        remindable_type: backendType,
        remindable_id: remindableId,
        remind_at: remindAt,
        message: message || undefined,
      };

      if (selectedRecipientIds.length > 0) {
        payload.recipient_user_ids = selectedRecipientIds;
      }

      await api.post('/api/v1/reminders', payload);
      onSaved();
      onClose();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const messages: string[] = [];
      if (apiErr.response?.data?.errors) {
        Object.values(apiErr.response.data.errors).forEach((errs) => messages.push(...errs));
      }
      const msg = apiErr.response?.data?.message;
      if (msg && messages.length === 0) messages.push(msg);
      setError(messages.length > 0 ? messages.join(' ') : 'Hatırlatıcı oluşturulurken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const typeLabel = remindableType === 'task' ? 'Görev' : 'Etkinlik';
  const selectedCount = selectedRecipientIds.length;
  const recipientLabel = selectedCount === 0
    ? 'Kendime hatırlat'
    : `${selectedCount} kişiye hatırlat`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      align="start"
      overlayClassName="overflow-y-auto bg-black/50 p-2 pt-2 sm:p-3 sm:pt-4 md:p-4 md:pt-10"
      panelUnstyled
      panelClassName="w-full max-w-none rounded-t-xl bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-md sm:rounded-xl sm:p-5 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
      labelledBy="reminder-modal-title"
      data-testid="reminder-modal"
    >
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 sm:h-9 sm:w-9">
              <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <div>
              <h2 id="reminder-modal-title" className="text-base font-bold text-slate-900 sm:text-lg">
                Hatırlatıcı Ekle
              </h2>
              <p className="max-w-[220px] truncate text-xs text-slate-500 sm:max-w-xs">
                {typeLabel}: {remindableTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="reminder-modal-close"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Kapat"
            title="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 sm:mb-4 sm:px-4 sm:py-2.5 sm:text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">Hatırlatma Zamanı</label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="datetime-local"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
                data-testid="reminder-time"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">Mesaj (opsiyonel)</label>
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="reminder-message"
              placeholder="Hatırlatıcı mesajı"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-slate-700">Alıcılar</label>
            <button
              type="button"
              onClick={() => setRecipientDropdownOpen((p) => !p)}
              data-testid="reminder-recipients-toggle"
              className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span>{recipientLabel}</span>
              <ChevronDown className={`h-4 w-4 text-slate-500 transition ${recipientDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {recipientDropdownOpen && (
              <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                {uniqueRecipients.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-500">Seçilebilecek kullanıcı yok.</p>
                )}
                {uniqueRecipients.map((u) => {
                  const selected = selectedRecipientIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleRecipient(u.id)}
                      data-testid={`reminder-recipient-${u.id}`}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <span>
                        {u.name} {u.surname}
                        <span className="ml-1 text-xs text-slate-500">{u.email}</span>
                      </span>
                      <div
                        className={`flex h-5 w-5 items-center justify-center rounded border ${
                          selected ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {selected && <Check className="h-3.5 w-3.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            data-testid="reminder-cancel"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={!remindAt || saving}
            data-testid="reminder-save"
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <LoadingSpinner size="sm" className="text-white" />}
            Hatırlatıcı Oluştur
          </button>
        </div>
    </Modal>
  );
}
